import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';

import { Button } from '#/components/Button/Button';
import { Markdown } from '#/components/Markdown';
import { CONTENT_MAX_WIDTH_PX } from '#/service/constants';
import { remify } from '#/service/utils';

export const COLUMN_GAP_PX = 16;

/**
 * Calculate the width for a field in a multi-column row layout
 * @param columns - Number of columns in the row (e.g., 2 for 50/50, 3 for 33/33/33)
 * @returns CSS calc string with proper gap adjustment
 */
export const getFieldWidth = (columns: number): string => {
  const percentage = 100 / columns;
  const gapAdjustment = (COLUMN_GAP_PX * (columns - 1)) / columns;

  return `calc(${percentage}% - ${gapAdjustment}px)`;
};

/** Container for a button group */
export const FormButtonContainer = styled.div`
  display: grid;
  grid-row-gap: ${remify(10)};
  margin: ${remify(24)} 0;
  width: 100%;
  flex: 0 0 100%;
`;

/** Basic styled form button */
export const FormButton = styled(Button)`
  width: 100%;
  justify-content: center;
`;

export const Form = styled.form`
  width: 100%;
  max-width: ${remify(CONTENT_MAX_WIDTH_PX)};
  display: flex;
  flex-wrap: wrap;
  column-gap: ${remify(COLUMN_GAP_PX)};
`;

Form.displayName = 'Form';

export const FormPlanContainer = styled.div`
  display: grid;
  row-gap: ${remify(20)};
`;

FormPlanContainer.displayName = 'FormPlanContainer';

const ARROW_SIZE = 12;
const ARROW_MARGIN = 4;

export const StyledReactTooltip = styled(ReactTooltip)`
  /* basic styling */
  && {
    font: var(--fontStyleFootnote);
    background-color: var(--pageSecondaryBackground);
    color: var(--pageParagraphForeground);
    padding: ${remify(24)};
    transition-property: opacity, visibility;
    transition-duration: 300ms;
    border-radius: ${remify(8)};
    filter: drop-shadow(0 0 ${remify(4)} var(--shadowColor));
  }

  &&.show {
    opacity: 1;
  }

  /* arrow size, color, and position */
  &&::before {
    border-top-color: var(--pageSecondaryBackground);
  }

  &&&::after {
    border-width: ${remify(ARROW_SIZE)};
  }

  &&.place-left {
    margin-left: ${remify(-ARROW_MARGIN - ARROW_SIZE)};

    &::after {
      border-right-width: 0;
      border-left-color: var(--pageSecondaryBackground);
      top: 50%;
      right: ${remify(-ARROW_SIZE)};
      margin-top: ${remify(-ARROW_SIZE)};
    }
  }

  &&.place-right {
    margin-left: ${remify(ARROW_MARGIN + ARROW_SIZE)};

    &::after {
      border-left-width: 0;
      border-right-color: var(--pageSecondaryBackground);
      top: 50%;
      left: ${remify(-ARROW_SIZE)};
      margin-top: ${remify(-ARROW_SIZE)};
    }
  }

  &&.place-top {
    margin-top: ${remify(-ARROW_MARGIN - ARROW_SIZE)};

    &::after {
      border-bottom-width: 0;
      border-top-color: var(--pageSecondaryBackground);
      left: 50%;
      bottom: ${remify(-ARROW_SIZE)};
      margin-left: ${remify(-ARROW_SIZE)};
    }
  }

  &&.place-bottom {
    margin-top: ${remify(ARROW_MARGIN + ARROW_SIZE)};

    &::after {
      border-top-width: 0;
      border-bottom-color: var(--pageSecondaryBackground);
      left: 50%;
      top: ${remify(-ARROW_SIZE)};
      margin-left: ${remify(-ARROW_SIZE)};
    }
  }
`;

StyledReactTooltip.displayName = 'StyledReactTooltip';

export const FormMarkdown = styled(Markdown)`
  font: var(--fontStyleCaption1);

  p {
    margin: ${remify(3)} 0;
  }
`;
